import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "value", "hideOnClick", "setValueOnClick", "shouldRegisterItem", "focusOnHover", "getItem"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback } from 'react';
import { isTextField, getPopupItemRole } from 'ariakit-utils/dom';
import { isOpeningInNewTab } from 'ariakit-utils/events';
import { hasFocus } from 'ariakit-utils/focus';
import { useBooleanEvent, useEvent, useWrapElement } from 'ariakit-utils/hooks';
import { queueMicrotask } from 'ariakit-utils/misc';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { useCompositeHover } from '../composite/composite-hover.js';
import { useCompositeItem } from '../composite/composite-item.js';
import { C as ComboboxContext, a as ComboboxItemValueContext } from '../__utils-8b39082b.js';
import { jsx } from 'react/jsx-runtime';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a combobox item.
 * @see https://ariakit.org/components/combobox
 * @example
 * ```jsx
 * const state = useComboboxState();
 * const props = useComboboxItem({ state, value: "value" });
 * <Role {...props} />
 * ```
 */

var useComboboxItem = createHook(function (_ref) {
  var _state6, _state7;

  var state = _ref.state,
      value = _ref.value,
      _ref$hideOnClick = _ref.hideOnClick,
      hideOnClick = _ref$hideOnClick === void 0 ? value != null : _ref$hideOnClick,
      _ref$setValueOnClick = _ref.setValueOnClick,
      setValueOnClick = _ref$setValueOnClick === void 0 ? true : _ref$setValueOnClick,
      _ref$shouldRegisterIt = _ref.shouldRegisterItem,
      shouldRegisterItem = _ref$shouldRegisterIt === void 0 ? true : _ref$shouldRegisterIt,
      _ref$focusOnHover = _ref.focusOnHover,
      focusOnHover = _ref$focusOnHover === void 0 ? false : _ref$focusOnHover,
      getItemProp = _ref.getItem,
      props = _objectWithoutProperties(_ref, _excluded);

  state = useStore(state || ComboboxContext, ["setValue", "move", "hide", "baseRef", "contentElement", "mounted"]);
  var getItem = useCallback(function (item) {
    var nextItem = _objectSpread(_objectSpread({}, item), {}, {
      value: value
    });

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [value, getItemProp]);
  var onClickProp = props.onClick;
  var setValueOnClickProp = useBooleanEvent(setValueOnClick);
  var hideOnClickProp = useBooleanEvent(hideOnClick);
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isOpeningInNewTab(event)) return;

    if (value != null && setValueOnClickProp(event)) {
      var _state;

      (_state = state) == null ? void 0 : _state.setValue(value);
    }

    if (hideOnClickProp(event)) {
      var _state2, _state3; // When ComboboxList is used instead of ComboboxPopover, state.hide()
      // does nothing. The focus will not be moved over to the combobox
      // input automatically. So we need to move manually here.


      (_state2 = state) == null ? void 0 : _state2.move(null);
      (_state3 = state) == null ? void 0 : _state3.hide();
    }
  });
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    var _state4;

    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    var baseElement = (_state4 = state) == null ? void 0 : _state4.baseRef.current;
    if (!baseElement) return;
    if (hasFocus(baseElement)) return; // When the combobox is not working with virtual focus, the items will
    // receive DOM focus. Therefore, pressing printable keys will not fill
    // the text field. So we need to programmatically focus on the text
    // field when the user presses printable keys.

    var printable = event.key.length === 1;

    if (printable || event.key === "Backspace" || event.key === "Delete") {
      queueMicrotask(function () {
        return baseElement.focus();
      });

      if (isTextField(baseElement)) {
        var _state5; // If the combobox element is a text field, we should update the
        // state value with the current's element value. This is necessary
        // because the value may temporarily change based on the currently
        // selected item, but it'll be reset to the original value when the
        // combobox input is focused.


        (_state5 = state) == null ? void 0 : _state5.setValue(baseElement.value);
      }
    }
  });
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(ComboboxItemValueContext.Provider, {
      value: value,
      children: element
    });
  }, [value]);
  props = _objectSpread(_objectSpread({
    role: getPopupItemRole((_state6 = state) == null ? void 0 : _state6.contentElement),
    children: value
  }, props), {}, {
    onClick: onClick,
    onKeyDown: onKeyDown
  });
  props = useCompositeItem(_objectSpread(_objectSpread({
    state: state
  }, props), {}, {
    getItem: getItem,
    // We only register the item on the state when the popover is open so we
    // don't try to move focus to hidden items when pressing arrow keys.
    shouldRegisterItem: ((_state7 = state) == null ? void 0 : _state7.mounted) && shouldRegisterItem
  }));
  props = useCompositeHover(_objectSpread({
    state: state,
    focusOnHover: focusOnHover
  }, props));
  return props;
});
/**
 * A component that renders a combobox item inside a combobox list or popover.
 * The `role` prop will be automatically set based on the `ComboboxList` or
 * `ComboboxPopover` own `role` prop. For example, if the `ComboboxPopover`
 * component's `role` prop is set to `listbox` (default), the `ComboboxItem`
 * `role` will be set to `option`. By default, the `value` prop will be rendered
 * as the children, but this can be overriden.
 * @see https://ariakit.org/components/combobox
 * @example
 * ```jsx
 * const combobox = useComboboxState();
 * <Combobox state={combobox} />
 * <ComboboxPopover state={combobox}>
 *   <ComboboxItem value="Item 1" />
 *   <ComboboxItem value="Item 2" />
 *   <ComboboxItem value="Item 3" />
 * </ComboboxPopover>
 * ```
 */

var ComboboxItem = createMemoComponent(function (props) {
  var htmlProps = useComboboxItem(props);
  return createElement("div", htmlProps);
});
export { ComboboxItem, useComboboxItem };